import { getAuthHeaders } from "../lib/auth";
import { useState, useRef } from "react";
import PharmatechLayout from "../components/PharmatechLayout";
import { Search, Pill, AlertTriangle, CheckCircle2, Baby, ChevronDown, ChevronUp, X, BookOpen } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

export default function PharmaSearchPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const timeout = useRef(null);

  const search = (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); return; }
    clearTimeout(timeout.current);
    timeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/pharmatech/drugs/search?q=${encodeURIComponent(q)}`, { headers: getHeaders() });
        setResults(r.data.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
  };

  const loadDetail = async (dci) => {
    try {
      const r = await axios.get(`${API}/pharmatech/drugs/${encodeURIComponent(dci)}`, { headers: getHeaders() });
      setDetail(r.data.drug);
      setSelected(dci);
    } catch { setDetail(null); }
  };

  return (
    <PharmatechLayout title="Google du Medicament" subtitle="Moteur de recherche pharmaceutique africain">
      <div data-testid="pharma-search-page" className="max-w-4xl mx-auto space-y-6">
        {/* Search bar */}
        <div className="relative">
          <div className="flex items-center bg-white border-2 border-blue-200 rounded-2xl shadow-lg focus-within:border-blue-400 transition-colors overflow-hidden">
            <Search className="w-5 h-5 text-blue-400 ml-5 flex-shrink-0" />
            <input data-testid="drug-search-input" value={query} onChange={e => search(e.target.value)}
              className="flex-1 px-4 py-4 text-base text-slate-800 placeholder-slate-400 focus:outline-none bg-transparent"
              placeholder="Rechercher par nom, DCI, classe therapeutique..." autoFocus />
            {query && <button onClick={() => { setQuery(""); setResults([]); setDetail(null); setSelected(null); }} className="mr-4 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>}
          </div>
        </div>

        <div className="flex gap-6">
          {/* Results list */}
          <div className={`${detail ? "w-80 flex-shrink-0" : "flex-1"} space-y-2`}>
            {results.length === 0 && query.length >= 2 && !loading && (
              <p className="text-center text-sm text-slate-400 py-8">Aucun resultat pour "{query}"</p>
            )}
            {results.length === 0 && query.length < 2 && !loading && (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Google du Medicament Africain</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">Recherchez parmi les medicaments courants en Cote d'Ivoire. Fiches completes avec posologies, interactions et contre-indications.</p>
                <div className="flex flex-wrap justify-center gap-2 mt-6">
                  {["Amoxicilline", "Paracetamol", "Metformine", "Ibuprofene", "Quinine", "Warfarine"].map(d => (
                    <button key={d} onClick={() => search(d)} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors">{d}</button>
                  ))}
                </div>
              </div>
            )}
            {results.map((r, i) => (
              <button key={i} onClick={() => loadDetail(r.dci)} data-testid={`search-result-${i}`}
                className={`w-full text-left p-4 rounded-xl border transition-all ${selected === r.dci ? "bg-blue-50 border-blue-300 shadow-sm" : "bg-white border-slate-200 hover:border-blue-200 hover:shadow-sm"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Pill className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-sm font-bold text-slate-800">{r.dci}</span>
                </div>
                <p className="text-[10px] text-slate-500 ml-6">{r.noms_commerciaux.join(", ")}</p>
                <span className="inline-block mt-1 ml-6 px-2 py-0.5 rounded text-[9px] font-medium bg-slate-100 text-slate-600">{r.classe}</span>
              </button>
            ))}
          </div>

          {/* Drug detail panel */}
          {detail && (
            <div className="flex-1 space-y-4" data-testid="drug-detail-panel">
              <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">{detail.dci}</h2>
                    <p className="text-xs text-slate-500">{detail.classe} | ATC: {detail.classe_atc}</p>
                  </div>
                  <button onClick={() => { setDetail(null); setSelected(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
                </div>

                <div className="space-y-1 mb-4">
                  <p className="text-xs text-slate-500 font-medium">Noms commerciaux</p>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.noms_commerciaux.map(n => <span key={n} className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">{n}</span>)}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-slate-50">
                    <p className="text-[10px] text-slate-500 uppercase font-medium mb-1">Formes</p>
                    <p className="text-xs text-slate-700">{detail.formes.join(", ")}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50">
                    <p className="text-[10px] text-slate-500 uppercase font-medium mb-1">Dosages</p>
                    <p className="text-xs text-slate-700">{detail.dosages.join(", ")}</p>
                  </div>
                </div>
              </div>

              {/* Posology */}
              <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-blue-500" /> Posologie</h3>
                <div className="space-y-2">
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <p className="text-[10px] text-blue-600 font-bold uppercase">Adulte</p>
                    <p className="text-xs text-slate-700 mt-1">{detail.posologie_adulte}</p>
                  </div>
                  {detail.posologie_pediatrique && (
                    <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-100">
                      <p className="text-[10px] text-cyan-600 font-bold uppercase">Pediatrique</p>
                      <p className="text-xs text-slate-700 mt-1">{detail.posologie_pediatrique}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contre-indications */}
              <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Contre-indications</h3>
                <div className="space-y-1">
                  {detail.contre_indications.map((ci, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded bg-red-50 border border-red-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                      <p className="text-xs text-slate-700">{ci}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Precautions */}
              <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Precautions d'emploi</h3>
                <div className="flex flex-wrap gap-1.5">
                  {detail.precautions.map((p, i) => <span key={i} className="px-2 py-1 rounded-lg text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">{p}</span>)}
                </div>
              </div>

              {/* Grossesse & Allaitement */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
                  <h4 className="text-xs font-bold text-slate-800 mb-2 flex items-center gap-1"><Baby className="w-3.5 h-3.5 text-pink-500" /> Grossesse</h4>
                  <StatusBadge text={detail.grossesse} />
                </div>
                <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
                  <h4 className="text-xs font-bold text-slate-800 mb-2 flex items-center gap-1"><Baby className="w-3.5 h-3.5 text-pink-500" /> Allaitement</h4>
                  <StatusBadge text={detail.allaitement} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PharmatechLayout>
  );
}

function StatusBadge({ text }) {
  const t = (text || "").toLowerCase();
  const isOk = t.includes("autorise") || t.includes("possible") || t.includes("reference");
  const isDanger = t.includes("contre-indique") || t.includes("contre indique");
  return (
    <div className={`p-2 rounded-lg text-xs ${isDanger ? "bg-red-50 text-red-700 border border-red-200" : isOk ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
      {isDanger ? <AlertTriangle className="w-3 h-3 inline mr-1" /> : isOk ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : null}
      {text}
    </div>
  );
}
